import 'C:\\Projects\\genkei\\zumeco\\static\\shared\\data\\DoodleShape.tsx.esbuild-plugin-linaria.css';
import 'C:\\Projects\\genkei\\zumeco\\static\\shared\\data\\DoodleShape.tsx.esbuild-plugin-windicss.css';
import { css } from '@linaria/core';
import { mdiCheckboxBlankOutline, mdiCheckboxMarked } from '@mdi/js';
import { ComponentChildren, JSX, render } from 'preact';
import { svgPathBbox } from 'svg-path-bbox';
import svgPath from 'svgpath';
import type { DoodleLayerId, LineEndType } from '../../../server/src/models';
import { shiftKey } from '../shiftKey';
import { deleteControlCharacters } from '../utils';
export type DoodleShape = DoodleLine | DoodleRect | DoodleEllipse | DoodlePath | DoodleText | DoodleChecklist | DoodleImage;
interface DoodlePoint {
  readonly x: number;
  readonly y: number;
}
export interface DoodleBoundingBox {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}
export interface DoodleHandle<T extends DoodleDrawable<any>> {
  readonly x: number;
  readonly y: number;
  readonly cursor: 'move' | 'ew-resize' | 'ns-resize' | 'nesw-resize' | 'nwse-resize' | 'nw-resize';
  move(dx: number, dy: number): Partial<T['properties']>;
}
abstract class DoodleDrawable<Properties extends DoodleCommonProperties> {
  abstract readonly properties: Properties;
  abstract readonly bbox: DoodleBoundingBox;
  abstract readonly p1: DoodlePoint;
  abstract readonly vdom: JSX.Element;
  abstract readonly handles: readonly DoodleHandle<this>[];
  abstract move(dx: number, dy: number, paperWidth: number, paperHeight: number): this;
  private _transformMatrix?: DOMMatrixReadOnly;
  get transformMatrix() {
    return this._transformMatrix = this._transformMatrix ?? new DOMMatrixReadOnly(this.properties.transform);
  }
  with(properties: Partial<Properties>): this {
    type self = this;
    return new (this.constructor as new (properties: any) => self)({
      ...this.properties,
      ...properties
    });
  }
}
interface DoodleCommonProperties {
  readonly id: string;
  readonly layerId: DoodleLayerId;
  readonly userName?: string;
  readonly userColor?: string;
  readonly editable: boolean;
  readonly transform?: string;
}
const commonProps = (properties: DoodleCommonProperties) => ({
  key: properties.id,
  'data-id': properties.id,
  'data-editable': properties.editable ? '' : undefined,
  transform: properties.transform
});
interface DoodleStrokeProperties {
  readonly color: string;
  readonly strokeWidth: number;
  readonly strokeDasharray?: readonly number[];
  readonly fill?: number; // 0: transparent, 1: opaque
}
const strokeProps = (shape: DoodleStrokeProperties) => ({
  fill: shape.fill! > 0 ? shape.color : 'none',
  'fill-opacity': 0 < shape.fill! && shape.fill! < 1 ? shape.fill : undefined,
  stroke: shape.color,
  'stroke-width': shape.strokeWidth,
  'stroke-dasharray': shape.strokeDasharray?.join(' ')
});
function keepAspect<T>(w: number, h: number, move: (dx: number, dy: number) => T, always = false): (dx: number, dy: number) => T {
  return (dx, dy) => {
    if ((always || shiftKey) && w && h) {
      return move(Math.round(dy * w / h), dy);
    } else {
      return move(dx, dy);
    }
  };
}
const LineEnd = ({
  lineEnd,
  x1,
  y1,
  x2,
  y2
}: {
  lineEnd: LineEndType;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}) => {
  switch (lineEnd) {
    case 'arrow':
      {
        const angle = Math.atan2(y2 - y1, x2 - x1);
        const a1 = angle + Math.PI * 5 / 6;
        const a2 = angle + Math.PI * 7 / 6;
        const r = 12;
        return <path d={`M${x2 + r * Math.cos(a1)} ${y2 + r * Math.sin(a1)}L${x2} ${y2}L${x2 + r * Math.cos(a2)} ${y2 + r * Math.sin(a2)}`} />;
      }
  }
  return null;
};
interface DoodleLineProperties extends DoodleCommonProperties, DoodleStrokeProperties {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly lineEnd1?: LineEndType;
  readonly lineEnd2?: LineEndType;
}
export class DoodleLine extends DoodleDrawable<DoodleLineProperties> {
  readonly type = 'line';
  readonly p1: DoodlePoint;
  readonly bbox: DoodleBoundingBox;
  readonly vdom: JSX.Element;
  readonly handles: readonly DoodleHandle<DoodleLine>[];
  constructor(readonly properties: DoodleLineProperties) {
    super();
    const {
      x1,
      y1,
      x2,
      y2,
      lineEnd1,
      lineEnd2
    } = properties;
    this.p1 = {
      x: x1,
      y: y1
    };
    this.bbox = {
      x: Math.min(x1, x2) - 8,
      y: Math.min(y1, y2) - 8,
      w: Math.abs(x1 - x2) + 16,
      h: Math.abs(y1 - y2) + 16
    };
    const {
      'stroke-dasharray': strokeDasharray,
      ..._strokeProps
    } = strokeProps(properties);
    this.vdom = <g {...commonProps(properties)} {..._strokeProps}>
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke-dasharray={strokeDasharray} />
        <LineEnd lineEnd={lineEnd1} x1={x2} y1={y2} x2={x1} y2={y1} />
        <LineEnd lineEnd={lineEnd2} x1={x1} y1={y1} x2={x2} y2={y2} />
      </g>;
    this.handles = [{
      x: x1,
      y: y1,
      cursor: 'nw-resize',
      move: keepAspect(x1 - x2, y1 - y2, (dx, dy) => ({
        x1: x1 + dx,
        y1: y1 + dy
      }))
    }, {
      x: x2,
      y: y2,
      cursor: 'nw-resize',
      move: keepAspect(x1 - x2, y1 - y2, (dx, dy) => ({
        x2: x2 + dx,
        y2: y2 + dy
      }))
    }];
  }
  move(dx: number, dy: number) {
    return this.with({
      x1: this.properties.x1 + dx,
      y1: this.properties.y1 + dy,
      x2: this.properties.x2 + dx,
      y2: this.properties.y2 + dy
    });
  }
}
const HatchingPatternDefs = ({
  id,
  color,
  coarseness
}: {
  id: string;
  color: string;
  coarseness: number;
}) => <defs>
    <pattern id={id} width={6 * coarseness} height={6 * coarseness} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
      <line x1={3 * coarseness} y1="0" x2={3 * coarseness} y2={6 * coarseness} stroke-width="1" stroke={color} />
    </pattern>
  </defs>;
const rectHandles = ({
  x,
  y,
  w,
  h: _h
}: DoodleBoundingBox, keepAspectAlways = false): DoodleHandle<DoodleRect>[] => {
  const cursorTopLeft = 'nwse-resize';
  const cursorTopRight = 'nesw-resize';
  const cursorUpDown = 'ns-resize';
  const cursorLeftRight = 'ew-resize';
  // prettier-ignore
  return [{
    x,
    y,
    cursor: cursorTopLeft,
    move: keepAspect(w, _h, (dx, dy) => ({
      x: x + dx,
      y: y + dy,
      w: w - dx,
      h: _h - dy
    }), keepAspectAlways)
  }, {
    x: x + w,
    y,
    cursor: cursorTopRight,
    move: keepAspect(w, -_h, (dx, dy) => ({
      y: y + dy,
      w: w + dx,
      h: _h - dy
    }), keepAspectAlways)
  }, {
    x,
    y: y + _h,
    cursor: cursorTopRight,
    move: keepAspect(w, -_h, (dx, dy) => ({
      x: x + dx,
      w: w - dx,
      h: _h + dy
    }), keepAspectAlways)
  }, {
    x: x + w,
    y: y + _h,
    cursor: cursorTopLeft,
    move: keepAspect(w, _h, (dx, dy) => ({
      w: w + dx,
      h: _h + dy
    }), keepAspectAlways)
  }, {
    x: x + w / 2,
    y,
    cursor: cursorUpDown,
    move: (_, dy) => ({
      y: y + dy,
      h: _h - dy
    })
  }, {
    x: x + w / 2,
    y: y + _h,
    cursor: cursorUpDown,
    move: (_, dy) => ({
      h: _h + dy
    })
  }, {
    x,
    y: y + _h / 2,
    cursor: cursorLeftRight,
    move: dx => ({
      x: x + dx,
      w: w - dx
    })
  }, {
    x: x + w,
    y: y + _h / 2,
    cursor: cursorLeftRight,
    move: dx => ({
      w: w + dx
    })
  }];
};
interface DoodleRectProperties extends DoodleCommonProperties, DoodleStrokeProperties {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly fill?: number;
}
export class DoodleRect extends DoodleDrawable<DoodleRectProperties> {
  readonly properties: DoodleRectProperties;
  readonly type = 'rect';
  readonly p1: DoodlePoint;
  readonly bbox: DoodleBoundingBox;
  readonly vdom: JSX.Element;
  readonly handles: readonly DoodleHandle<DoodleRect>[];
  constructor(p: DoodleRectProperties) {
    super();
    const x = Math.min(p.x, p.x + p.w);
    const y = Math.min(p.y, p.y + p.h);
    const w = Math.abs(p.w);
    const _h = Math.abs(p.h);
    this.properties = {
      ...p,
      x,
      y,
      w,
      h: _h
    };
    this.p1 = {
      x,
      y
    };
    this.bbox = {
      x,
      y,
      w,
      h: _h
    };
    this.vdom = p.fill! < 0 ? <>
          <HatchingPatternDefs id={`fill-${p.id}`} color={p.color} coarseness={-p.fill!} />
          <rect {...commonProps(p)} {...strokeProps(p)} x={x} y={y} width={w} height={_h} fill={`url(#fill-${p.id})`} />
        </> : <rect {...commonProps(p)} {...strokeProps(p)} x={x} y={y} width={w} height={_h} />;
    this.handles = rectHandles(this.bbox);
  }
  move(dx: number, dy: number) {
    return this.with({
      x: this.properties.x + dx,
      y: this.properties.y + dy
    });
  }
}
interface DoodleEllipseProperties extends DoodleCommonProperties, DoodleStrokeProperties {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly fill?: number;
}
export class DoodleEllipse extends DoodleDrawable<DoodleEllipseProperties> {
  readonly properties: DoodleEllipseProperties;
  readonly type = 'ellipse';
  readonly p1: DoodlePoint;
  readonly bbox: DoodleBoundingBox;
  readonly vdom: JSX.Element;
  readonly handles: readonly DoodleHandle<DoodleEllipse>[];
  constructor(p: DoodleEllipseProperties) {
    super();
    const x = Math.min(p.x, p.x + p.w);
    const y = Math.min(p.y, p.y + p.h);
    const w = Math.abs(p.w);
    const _h = Math.abs(p.h);
    this.properties = {
      ...p,
      x,
      y,
      w,
      h: _h
    };
    const rx = w / 2;
    const ry = _h / 2;
    this.p1 = {
      x: x + w * 0.145,
      y: y + _h * 0.145
    };
    this.bbox = {
      x,
      y,
      w,
      h: _h
    };
    this.vdom = p.fill! < 0 ? <>
          <HatchingPatternDefs id={`fill-${p.id}`} color={p.color} coarseness={-p.fill!} />
          <ellipse {...commonProps(p)} {...strokeProps(p)} cx={x + rx} cy={y + ry} rx={rx} ry={ry} fill={`url(#fill-${p.id})`} />
        </> : <ellipse {...commonProps(p)} {...strokeProps(p)} cx={x + rx} cy={y + ry} rx={rx} ry={ry} />;
    this.handles = rectHandles(this.bbox);
  }
  move(dx: number, dy: number) {
    return this.with({
      x: this.properties.x + dx,
      y: this.properties.y + dy
    });
  }
}
const pathFirstPoint = (d: string) => {
  const match = d.match(/M\s*(-?\d*(?:\.\d*)?)\s*[,\s]\s*(-?\d*(?:\.\d*)?)/);
  return match ? {
    x: +match[1],
    y: +match[2]
  } : undefined;
};
interface DoodlePathProperties extends DoodleCommonProperties, DoodleStrokeProperties {
  readonly d: string;
}
export class DoodlePath extends DoodleDrawable<DoodlePathProperties> {
  readonly type = 'path';
  readonly vdom: JSX.Element;
  readonly handles: readonly DoodleHandle<DoodlePath>[] = [];
  private _bbox?: DoodleBoundingBox;
  get bbox(): DoodleBoundingBox {
    if (!this._bbox) {
      const margin = this.properties.strokeWidth / 2;
      const [left, top, right, bottom] = svgPathBbox(this.properties.d);
      this._bbox = {
        x: left - margin,
        y: top - margin,
        w: right - left + margin + margin,
        h: bottom - top + margin + margin
      };
    }
    return this._bbox;
  }
  get p1(): DoodlePoint {
    return pathFirstPoint(this.properties.d) ?? {
      x: -9999,
      y: -9999
    };
  }
  constructor(readonly properties: DoodlePathProperties) {
    super();
    this.vdom = <path {...commonProps(properties)} {...strokeProps(properties)} d={properties.d} />;
  }
  move(dx: number, dy: number) {
    return this.with({
      d: svgPath(this.properties.d).translate(dx, dy).toString()
    });
  }
  segments(): DoodlePathSegment[] {
    return this.properties.d.slice(1).split('M').map(segment => new DoodlePathSegment({
      ...this.properties,
      d: `M${segment}`
    }));
  }
}
export class DoodlePathSegment extends DoodlePath {
  constructor(properties: DoodlePathProperties) {
    super(properties);
    this.vdom.key = `${properties.id}:${properties.d.slice(8)}`; // どうやって key 割り当てたらいいでしょう？ d のハッシュかな？
  }
}
const splitLines = (s: string) => s.split(/\r\n|\r|\n/);
const calculateTextBBox = ({
  lines,
  fontSize,
  lineGap
}: {
  lines: readonly string[];
  fontSize: number;
  lineGap: number;
}) => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.style.position = 'absolute';
  svg.style.visibility = 'hidden';
  document.body.appendChild(svg);
  const el = svg.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'text'));
  el.setAttribute('font-size', fontSize as string & number);
  lines.map((s, i) => {
    const tspan = el.appendChild(document.createElementNS('http://www.w3.org/2000/svg', 'tspan'));
    tspan.textContent = s || ' ';
    tspan.setAttribute('x', 0 as string & number);
    tspan.setAttribute('y', i * (fontSize + lineGap) - lineGap as string & number);
  });
  const bbox = el.getBBox();
  document.body.removeChild(svg);
  // Windows と Mac とで getBBox() の高さ計算が異なるので自前算出します。
  bbox.height = lines.length * (fontSize + lineGap);
  return bbox;
};
const MultilineText = ({
  lines,
  lineGap,
  ...props
}: preact.SVGAttributes<SVGTextElement> & {
  x: number;
  y: number;
  'font-size': number;
  lines: readonly string[];
  lineGap: number;
}) => <text {...props}>
    {lines.map((line, i) =>
  // Safari が dominant-baseline 未サポートなので dominant-baseline="text-before-edge" の代わりに dy=".9em" しています。 ".9em" は感覚値です...。
  <tspan key={i} x={props.x} y={props.y + i * (props['font-size'] + lineGap)} dy=".9em">
        {line}
      </tspan>)}
  </text>;
interface DoodleTextProperties extends DoodleCommonProperties {
  readonly text: string;
  readonly x: number;
  readonly y: number;
  readonly color: string;
  readonly fontSize: number;
  readonly enclosure?: 'rect' | 'circle' | 'rect:fill' | 'circle:fill';
}
export class DoodleText extends DoodleDrawable<DoodleTextProperties> {
  readonly type = 'text';
  readonly p1: DoodlePoint;
  readonly bbox: DoodleBoundingBox;
  readonly vdom: JSX.Element;
  readonly handles: readonly DoodleHandle<DoodleText>[] = [];
  constructor(readonly properties: DoodleTextProperties) {
    super();
    const {
      x,
      y,
      color,
      fontSize,
      enclosure
    } = properties;
    // 制御文字が含まれるテキストで SVG -> PNG 変換時にエラーが発生した（BS (String.fromCharCode(8)) で確認）ので、半角スペースに変えておきます。
    const lines = splitLines(deleteControlCharacters(properties.text));
    const lineGap = fontSize * 0.25;
    const {
      width,
      height
    } = calculateTextBBox({
      lines,
      fontSize,
      lineGap
    });
    const padding = 6;
    this.p1 = {
      x: x - padding,
      y: y - padding
    };
    this.bbox = {
      x: x - padding,
      y: y - padding,
      w: width + padding + padding,
      h: height + padding + padding
    };
    const enclosurePadding = 4;
    this.vdom = <g {...commonProps(properties)}>
        {enclosure === 'circle' || enclosure === 'circle:fill' ? <circle cx={x + width / 2} cy={y + height / 2} r={Math.max(width, height) / 2 + enclosurePadding} fill={enclosure === 'circle:fill' ? color : 'transparent'} stroke={enclosure === 'circle' ? color : 'none'} stroke-width={2} /> : <rect x={x - enclosurePadding} y={y - enclosurePadding} width={width + enclosurePadding + enclosurePadding} height={height + enclosurePadding + enclosurePadding} fill={enclosure === 'rect:fill' ? color : 'transparent'} stroke={enclosure === 'rect' ? color : 'none'} stroke-width={2} />}
        <MultilineText x={x} y={y} font-size={fontSize} fill={enclosure === 'rect:fill' || enclosure === 'circle:fill' ? 'white' : color} lines={lines} lineGap={lineGap} />
      </g>;
  }
  move(dx: number, dy: number) {
    return this.with({
      x: this.properties.x + dx,
      y: this.properties.y + dy
    });
  }
}
export interface DoodleChecklistItem {
  readonly label: string;
  readonly order: number;
  readonly checked?: {
    readonly color?: string;
    readonly timestamp: number;
    readonly userDisplayName: string;
  };
  readonly disabled?: boolean;
}
export interface DoodleChecklistItems {
  readonly [itemId: string]: DoodleChecklistItem;
}
const formatChecklistItemChecked = (item: DoodleChecklistItem) => item.checked?.userDisplayName ?? '';
const ChecklistItem = ({
  id,
  itemId,
  item,
  x,
  y,
  fontSize,
  defaultColor,
  labelLines
}: {
  id: string;
  itemId: string;
  item: DoodleChecklistItem;
  x: number;
  y: number;
  fontSize: number;
  defaultColor: string;
  labelLines: readonly string[];
}) => <g data-checklist-item data-shape-id={id} data-item-id={itemId} data-disabled={item.disabled ? '' : undefined} fill={item.checked?.color ?? defaultColor} className={"g1yyad5j"}>
    {/* チェックボックス（+ 余白もクリックできるようにするための透明の箱） */}
    <path d={item.checked ? mdiCheckboxMarked : mdiCheckboxBlankOutline} transform={`translate(${x} ${y}) scale(${fontSize / 24})`} />
    <rect x={x} y={y} width="1.5em" height={fontSize} fill="transparent" />

    {/* チェックした人 */}
    {item.checked && <text x={x} y={y + fontSize * labelLines.length} dy=".9em" font-size={fontSize * 0.5} opacity=".8">
        {formatChecklistItemChecked(item)}
      </text>}

    {/* 項目 */}
    <MultilineText x={x + fontSize * 1.25} y={y} font-size={fontSize} lines={labelLines} lineGap={0} />
  </g>;
const calculateSvgVDomBBox = (vdom: ComponentChildren) => {
  const div = document.body.appendChild(document.createElement('div'));
  div.style.position = 'absolute';
  div.style.visibility = 'hidden';
  render(<svg>{vdom}</svg>, div);
  const bbox = (div.firstElementChild as SVGSVGElement).getBBox();
  document.body.removeChild(div);
  return bbox;
};
interface DoodleChecklistProperties extends DoodleCommonProperties {
  readonly x: number;
  readonly y: number;
  readonly color: string;
  readonly fontSize: number;
  readonly items: DoodleChecklistItems;
}
export class DoodleChecklist extends DoodleDrawable<DoodleChecklistProperties> {
  readonly type = 'checklist';
  readonly p1: DoodlePoint;
  readonly bbox: DoodleBoundingBox;
  readonly vdom: JSX.Element;
  readonly handles: readonly DoodleHandle<DoodleChecklist>[] = [];
  private _deletable: boolean | undefined;
  get deletable() {
    return this._deletable ??= !Object.values(this.properties.items).some(item => item.checked);
  }
  constructor(readonly properties: DoodleChecklistProperties) {
    super();
    const {
      id,
      x,
      y,
      items,
      color,
      fontSize
    } = properties;
    const itemGap = fontSize * 1.25;
    const enclosurePadding = 8;
    let height = 0;
    const contents = Object.entries(items).sort(([, a], [, b]) => a.order - b.order).map(([itemId, item]) => {
      const _y = Math.round(y + height);
      // 制御文字が含まれるテキストで SVG -> PNG 変換時にエラーが発生した（BS (String.fromCharCode(8)) で確認）ので、半角スペースに変えておきます。
      const labelLines = splitLines(deleteControlCharacters(item.label));
      height += labelLines.length * fontSize + itemGap;
      return <ChecklistItem key={itemId} id={id} itemId={itemId} item={item} x={x + enclosurePadding} y={_y + enclosurePadding} fontSize={fontSize} defaultColor={color} labelLines={labelLines} />;
    });
    // すでに itemGap 分の余白があるのを加味して余白調整
    height -= fontSize * 0.75;
    const bbox = calculateSvgVDomBBox(contents);
    this.p1 = {
      x: x - 4,
      y: y - 4
    };
    this.bbox = {
      x: x - 4,
      y: y - 4,
      w: bbox.width + enclosurePadding + enclosurePadding + 8,
      h: height + enclosurePadding + enclosurePadding + 8
    };
    this.vdom = <g {...commonProps(properties)}>
        <rect x={x} y={y} width={bbox.width + enclosurePadding + enclosurePadding} height={height + enclosurePadding + enclosurePadding} stroke={color} stroke-width={2} fill="transparent" />
        {contents}
      </g>;
  }
  move(dx: number, dy: number, paperWidth: number, paperHeight: number) {
    const x = this.properties.x + dx;
    const y = this.properties.y + dy;
    return this.with({
      x: Math.min(Math.max(1, x), paperWidth - this.bbox.w + 7),
      y: Math.min(Math.max(1, y), paperHeight - this.bbox.h + 7)
    });
  }
}
interface DoodleImageProperties extends DoodleCommonProperties, DoodleStrokeProperties {
  readonly src: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}
export class DoodleImage extends DoodleDrawable<DoodleImageProperties> {
  readonly type = 'image';
  readonly p1: DoodlePoint;
  readonly bbox: DoodleBoundingBox;
  readonly vdom: JSX.Element;
  readonly handles: readonly DoodleHandle<DoodleImage>[];
  constructor(readonly properties: DoodleImageProperties) {
    super();
    const {
      src,
      x,
      y,
      strokeWidth
    } = properties;
    const w = Math.max(properties.w, 8);
    const _h = Math.max(properties.h, 8);
    this.p1 = {
      x,
      y
    };
    this.bbox = {
      x,
      y,
      w,
      h: _h
    };
    this.vdom = strokeWidth ? <g {...commonProps(properties)}>
        <image x={x} y={y} width={w} height={_h} href={src} />
        <rect {...strokeProps(properties)} x={x} y={y} width={w} height={_h} />
      </g> : <image {...commonProps(properties)} {...strokeProps(properties)} x={x} y={y} width={w} height={_h} href={src} />;
    this.handles = rectHandles(this.bbox, true).slice(0, 4);
  }
  move(dx: number, dy: number) {
    return this.with({
      x: this.properties.x + dx,
      y: this.properties.y + dy
    });
  }
}